import {
    BenchmarkFinalDE,
    createNqError,
    DownloadFinalDE,
    DownloadIntermediateDE,
    HintsDE,
    ModemFinalDE,
    NQValueDE,
    NQValueKind,
    PingFinalDE,
    PingIntermediateDE,
    PrepareResultDE,
    UploadFinalDE,
    UploadIntermediateDE
} from "@visonum/network-quality-sdk"
import { freeze } from "immer"
import * as R from "ramda"
import { NQRecord, NQRecordPrepareResult } from "./types"

/* pure */
export const createNQRecord = (nqValues: NQValueDE[], date: Date): NQRecord => {
    const record: NQRecord = {
        date: date,
        prepareResult: createNQRecordPrepareResult(findByKind([NQValueKind.PrepareResult], nqValues) as PrepareResultDE),
        benchmarkFinal: findByKind([NQValueKind.BenchmarkFinalAvailable, NQValueKind.BenchmarkFinalDidNotRun], nqValues) as BenchmarkFinalDE,
        downloadIntermediates: findNumberArrayByKind<DownloadIntermediateDE>(NQValueKind.DownloadIntermediate, nqValues, x => x.speed),
        downloadFinal: findByKind([NQValueKind.DownloadFinal], nqValues) as DownloadFinalDE,
        uploadIntermediates: findNumberArrayByKind<UploadIntermediateDE>(NQValueKind.UploadIntermediate, nqValues, x => x.speed),
        uploadFinal: findByKind([NQValueKind.UploadFinal], nqValues) as UploadFinalDE,
        pingIntermediates: findNumberArrayByKind<PingIntermediateDE>(NQValueKind.PingIntermediate, nqValues, x => x.pingTime),
        pingFinal: findByKind([NQValueKind.PingFinal], nqValues) as PingFinalDE,
        modemFinal: findByKind([NQValueKind.ModemFinal], nqValues) as ModemFinalDE,
        hints: (findByKind([NQValueKind.Hints], nqValues) as HintsDE).hints.map(h => h.id),
    }
    return freeze(record)
}

/* pure */
const findNumberArrayByKind = <T extends NQValueDE>(kind: NQValueKind, nqValues: NQValueDE[], g: (v: T) => number) =>
    nqValues.filter(v => v.kind === kind).map(v => v as T).map(g);

/**
* Searches an array for a value of a given selection of kinds and returns it.
* @kinds array of kinds to choose from
* @param nqValues values
* @throws if a value of the given type is not found, an exception is thrown
* pure
*/
function findByKind(kinds: NQValueKind[], nqValues: NQValueDE[]): NQValueDE {
    const candidate = nqValues.find(v => R.includes<NQValueKind>(v.kind, kinds));
    if (candidate != undefined) {
        return candidate
    } else {
        throw createNqError("Cl-6-132", "Missing NQValue during save", { kinds, nqValues });
    }
}

/* pure */
function createNQRecordPrepareResult(p: PrepareResultDE): NQRecordPrepareResult {
    return {
        init: {
            speedtest: {
                id: p.init.speedtest.id,
            },
            connection: {
                ip: p.init.connection.ip,
                isp: p.init.connection.isp,
                isCustomer: p.init.connection.isCustomer,
                ispFootprint: p.init.connection.ispFootprint,
                isVpnActive: p.init.connection.isVpnActive,
            },
            modem: {
                bookedDownloadSpeedMax: p.init.modem?.bookedDownloadSpeedMax,
                bookedDownloadSpeedAvg: p.init.modem?.bookedDownloadSpeedAvg,
                bookedDownloadSpeedMin: p.init.modem?.bookedDownloadSpeedMin,
                bookedUploadSpeedMax: p.init.modem?.bookedUploadSpeedMax,
                bookedUploadSpeedAvg: p.init.modem?.bookedUploadSpeedAvg,
                bookedUploadSpeedMin: p.init.modem?.bookedUploadSpeedMin,
                provisionedDownloadSpeed: p.init.modem?.provisionedDownloadSpeed,
                type: p.init.modem?.type,
                name: p.init.modem?.name,
                vendor: p.init.modem?.vendor,
            },
            cmts: p.init.cmts === null ? null : {
                isModemTestAvailable: p.init.cmts.isModemTestAvailable,
                vendor: p.init.cmts.vendor,
            },
            client: {
                device: p.init.client.device,
                browser: {
                    name: p.init.client.browser.name,
                    version: p.init.client.browser.version,
                    isCurrent: p.init.client.browser.isCurrent,
                }
            },
            communication: {
                modemSwapLink: p.init.communication.modemSwapLink,
                plannedImprovement: p.init.communication.plannedImprovement,
            },
            partialService: {
                downloadIndex: p.init.partialService.downloadIndex,
                uploadIndex: p.init.partialService.uploadIndex
            }
        },
        isIPv6: p.isIPv6,
    }
}
